export type FileSizeUnit = 'B' | 'KB' | 'MB' | 'GB' | 'TB';

/** Upload limit declared in decimal units, e.g. `{ value: 150, unit: 'MB' }`. */
export interface FileSizeLimit {
  value: number;
  unit: FileSizeUnit;
}

const UNIT_EXPONENT: Record<FileSizeUnit, number> = {
  B: 0,
  KB: 1,
  MB: 2,
  GB: 3,
  TB: 4,
};

const DISPLAY_UNITS = ['bytes', 'KB', 'MB', 'GB', 'TB'];

const DECIMAL_BASE = 1000;
const BINARY_BASE = 1024;

/**
 * Max allowed bytes for a limit. The limit is declared in decimal units
 * (shown to the user as "150 MB"), but the check uses the binary size
 * (150 MiB), so files that an OS reports as "150 MB" using binary units
 * are never wrongly rejected.
 */
export function fileSizeLimitToBytes(limit: FileSizeLimit): number {
  return limit.value * Math.pow(BINARY_BASE, UNIT_EXPONENT[limit.unit]);
}

/** True when the file size fits the limit (a size equal to the limit passes). */
export function isFileSizeWithinLimit(
  bytes: number,
  limit: FileSizeLimit
): boolean {
  return bytes <= fileSizeLimitToBytes(limit);
}

/** User-facing limit label, e.g. "150 MB". */
export function formatFileSizeLimit(limit: FileSizeLimit): string {
  return `${limit.value} ${limit.unit === 'B' ? 'bytes' : limit.unit}`;
}

/**
 * Formats a real file size in decimal units (base 1000), e.g. "1.50 MB".
 * Returns null for non-finite input.
 */
export function formatFileSize(bytes = 0, precision = 2): string | null {
  if (!isFinite(bytes)) {
    return null;
  }

  let size = bytes;
  let unitIndex = 0;

  while (size >= DECIMAL_BASE && unitIndex < DISPLAY_UNITS.length - 1) {
    size /= DECIMAL_BASE;
    unitIndex++;
  }

  return `${size.toFixed(precision)} ${DISPLAY_UNITS[unitIndex]}`;
}
